'use client';

import { useCommendaOSEntitySchema } from '@commenda/api/commenda-os-dsl';
import {
  Badge,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SingleCombobox,
} from '@commenda/kakashi';
import { useEffect, useMemo } from 'react';
import { COMMENDA_OS_DATA_SOURCE } from '../../constants';
import type { CommendaOSFieldConfig, DataSourceConfig, FormDSL } from '@commenda/types';
import { ContextBindingEditor } from './ContextBindingEditor';
import {
  getContextSourceFields,
  getEntityContextKeys,
} from './context-helpers';

export function GovernmentIdentifierEditor({
  props,
  dataSource,
  onUpdateProps,
  onDataSourceChange,
  currentFieldId,
  dsl,
}: {
  props: Record<string, unknown>;
  dataSource?: DataSourceConfig;
  onUpdateProps: (key: string, value: unknown) => void;
  onDataSourceChange: (dataSource: DataSourceConfig | undefined) => void;
  currentFieldId?: string;
  dsl?: FormDSL;
}) {
  const { entitySchema, isLoading } = useCommendaOSEntitySchema(
    'governmentIdentifier'
  );

  const identifierTypeOptions = useMemo(() => {
    if (!entitySchema?.filters?.identifierTypeId?.enumOptions) return [];
    return entitySchema.filters.identifierTypeId.enumOptions.map((opt) => ({
      value: opt.value,
      label: opt.label,
      description: (opt as { description?: string }).description,
    }));
  }, [entitySchema]);

  const selectedTypeId = (props?.identifierTypeId as string) || '';
  const mode = (props?.mode as 'read' | 'read-write') || 'read-write';
  const commendaOS = dataSource?.commendaOS;

  const contextSourceFields = useMemo(() => {
    if (!dsl) return [];
    return getContextSourceFields(dsl, currentFieldId, 'corporation');
  }, [dsl, currentFieldId]);

  const formContextKeys = useMemo(() => {
    const requirements = (
      dsl as unknown as {
        form?: { contextRequirements?: { required?: string[] } };
      }
    )?.form?.contextRequirements;
    return requirements?.required || [];
  }, [dsl]);

  useEffect(() => {
    if (!dataSource || dataSource.type !== COMMENDA_OS_DATA_SOURCE) {
      onDataSourceChange({
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity: 'governmentIdentifier',
          contextBindings: [],
        },
      });
    }
  }, [dataSource, onDataSourceChange]);

  const updateCommendaOS = (updates: Partial<CommendaOSFieldConfig>) => {
    onDataSourceChange({
      type: COMMENDA_OS_DATA_SOURCE,
      commendaOS: {
        entity: 'governmentIdentifier',
        contextBindings: commendaOS?.contextBindings,
        ...updates,
      },
    });
  };

  const selectedTypeMeta = identifierTypeOptions.find(
    (opt) => opt.value === selectedTypeId
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="txt-small-medium text-text-secondary">
          Business ID Configuration
        </h3>
        <Badge variant="gray">Commenda OS</Badge>
      </div>

      <div className="space-y-4 pl-4 border-l-2 border-border-secondary">
        {/* Identifier Key Selection */}
        <div className="space-y-2">
          <Label>Identifier Type</Label>
          {isLoading ? (
            <div className="flex items-center gap-2 p-2">
              <LoadingIndicator size={16} />
              <span className="txt-small-regular text-text-tertiary">
                Loading identifier types...
              </span>
            </div>
          ) : (
            <SingleCombobox
              options={identifierTypeOptions}
              value={selectedTypeId}
              setValue={(value) => onUpdateProps('identifierTypeId', value)}
              placeholder="Select identifier type..."
              searchPlaceholder="Search identifier types..."
            />
          )}
          {selectedTypeMeta?.description && (
            <p className="txt-xs-regular text-text-tertiary">
              {selectedTypeMeta.description}
            </p>
          )}
        </div>

        {/* Mode Selection */}
        <div className="space-y-2">
          <Label>Mode</Label>
          <Select
            value={mode}
            onValueChange={(value) => onUpdateProps('mode', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="read">Read Only</SelectItem>
              <SelectItem value="read-write">Read & Write</SelectItem>
            </SelectContent>
          </Select>
          <p className="txt-xs-regular text-text-tertiary">
            Read Only: Display value but don&apos;t allow editing. Read & Write:
            Display and save changes.
          </p>
        </div>

        {/* Context Source - corporationId binding (GovernmentIdentifier needs corporation context, not governmentIdentifier) */}
        <ContextBindingEditor
          entity="corporation"
          contextKeys={getEntityContextKeys('corporation')}
          contextBindings={commendaOS?.contextBindings}
          availableFields={contextSourceFields}
          formContextKeys={formContextKeys}
          onChange={(bindings) =>
            updateCommendaOS({ contextBindings: bindings })
          }
        />
      </div>
    </div>
  );
}
